import { GraphQLError } from "graphql";
import {
  DropdownMenu,
  Button,
  Text,
  TextInput,
  TextArea,
  Select,
} from "north.js";
import React, { useCallback, useMemo, useState } from "react";
import styled from "styled-components";
import { useQueryParam } from "use-query-params";

import { HardwareSkuType } from "src/api/types.generated";
import {
  Header,
  PageWrapper,
  Spinner,
  Modal,
  errorToast,
  successToast,
  Icon,
  InfoTag,
} from "src/shared/components";
import { Search } from "src/shared/components/Hardware";
import { CURRENT_EVENT_SLUG } from "src/shared/constants/event";
import { ChevronDownIcon } from "src/static/icons";
import { deviceBreakpoints } from "src/theme/deviceBreakpoints";
import globalConstants from "src/theme/globalConstants";

import { useCreateHardwareItemMutation } from "../../graphql/createHardwareItem.generated";
import { useCreateHardwareSkuMutation } from "../../graphql/createHardwareSku.generated";
import { useDeleteHardwareItemMutation } from "../../graphql/deleteHardwareItem.generated";
import { useGetRequestsQuery } from "../../graphql/getAllRequests.generated";
import { useUpdateHardwareItemMutation } from "../../graphql/updateHardwareItem.generated";
import { useUpdateHardwareSkuMutation } from "../../graphql/updateHardwareSku.generated";
import { THardwareInventoryItem } from "../../shared/hardwareModels";
import { useGetHardwareInfo } from "../../shared/useGetHardwareInfo";
import { getRandomHardwareLoadingMessage } from "../shared/loadingTextUtil";

import UploadSpreadsheet from "./uploadSpreadsheet";

const OrganizerInventory: React.FC = () => {
  const [query, setQuery] = useQueryParam<string>("search");
  const [filterParam] = useQueryParam<string>("filterParam");
  const [selectedCategory, setSelectedCategory] = useState<number>(
    filterParam ? parseInt(filterParam) : -1
  );
  const [tab, setTab] = useState<"inventory" | "uploadSpreadsheet">(
    "inventory"
  );
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<THardwareInventoryItem | null>(
    null
  );
  const [deletingItem, setDeletingItem] =
    useState<THardwareInventoryItem | null>(null);
  const [editForm, setEditForm] = useState({
    name: "",
    description: "",
    image: "",
    location: "",
    categoryId: 0,
    totalQuantity: 0,
    availableQuantity: 0,
  });
  const [createForm, setCreateForm] = useState({
    code: "",
    name: "",
    description: "",
    image: "",
    location: "",
    categoryId: 0,
    type: HardwareSkuType.Free,
    totalQuantity: 0,
    availableQuantity: 0,
  });

  const { refetch: refetchRequests } = useGetRequestsQuery();
  const [updateHardwareItem] = useUpdateHardwareItemMutation();
  const [updateHardwareSku] = useUpdateHardwareSkuMutation();
  const [createHardwareItem] = useCreateHardwareItemMutation();
  const [createHardwareSku] = useCreateHardwareSkuMutation();
  const [deleteHardwareItem] = useDeleteHardwareItemMutation();

  const onSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.currentTarget.value === "") {
        setQuery("");
      } else {
        setQuery(e.currentTarget.value);
      }
    },
    [setQuery]
  );

  const {
    hardwareCategories,
    inventoryItems,
    hardwareCategoriesDataLoading,
    hardwareSkusDataLoading,
    hardwareItemsLoading,
    refetchHardwareSkus,
    refetchHardwareItems,
  } = useGetHardwareInfo({ includeItems: true });

  const filteredHardwareItems = useMemo(() => {
    let filtered = [...inventoryItems];
    if (selectedCategory !== -1) {
      filtered = filtered.filter(
        (item) => item.sku.category_id === selectedCategory
      );
    }

    if (query && query.trim() !== "") {
      const lowercaseQuery = query.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          item.sku.name.toLowerCase().includes(lowercaseQuery) ||
          item.sku.description.toLowerCase().includes(lowercaseQuery)
      );
    }

    // Sort alphabetically by name
    return filtered.sort((a, b) => a.sku.name.localeCompare(b.sku.name));
  }, [inventoryItems, selectedCategory, query]);

  const loadingMessage = useMemo(() => {
    return getRandomHardwareLoadingMessage();
  }, []);

  const handleEditItem = useCallback((item: THardwareInventoryItem) => {
    setEditingItem(item);

    setEditForm({
      name: item.sku.name,
      description: item.sku.description || "",
      image: item.sku.image || "",
      location: item.sku.location || "",
      categoryId: item.sku.category_id,
      totalQuantity: item.total_quantity,
      availableQuantity: item.available_quantity,
    });
    setEditModalOpen(true);
  }, []);

  const handleSaveEdit = useCallback(async () => {
    if (!editingItem) return;

    try {
      // Update SKU information
      await updateHardwareSku({
        variables: {
          code: editingItem.sku.code,
          name: editForm.name,
          description: editForm.description,
          image: editForm.image,
          location: editForm.location,
          category_id: editForm.categoryId,
        },
      });

      await updateHardwareItem({
        variables: {
          id: editingItem.id,
          total_quantity: editForm.totalQuantity,
          available_quantity: editForm.availableQuantity,
        },
      });

      successToast("Item updated successfully");
      setEditModalOpen(false);
      setEditingItem(null);

      // Refetch all relevant data
      await Promise.all([
        refetchHardwareSkus(),
        refetchHardwareItems(),
        refetchRequests(),
      ]);
    } catch (e) {
      errorToast((e as GraphQLError)?.message || "Failed to update item");
    }
  }, [
    editingItem,
    editForm,
    updateHardwareSku,
    updateHardwareItem,
    refetchHardwareSkus,
    refetchHardwareItems,
    refetchRequests,
  ]);

  const handleCancelEdit = useCallback(() => {
    setEditModalOpen(false);
    setEditingItem(null);
  }, []);

  const handleCreateItem = useCallback(() => {
    setCreateForm({
      code: "",
      name: "",
      description: "",
      image: "",
      location: "",
      categoryId: 0,
      type: HardwareSkuType.Free,
      totalQuantity: 0,
      availableQuantity: 0,
    });
    setCreateModalOpen(true);
  }, []);

  const handleSaveCreate = useCallback(async () => {
    try {
      // Create the SKU first
      await createHardwareSku({
        variables: {
          code: createForm.code,
          name: createForm.name,
          description: createForm.description,
          image: createForm.image,
          location: createForm.location,
          category_id: createForm.categoryId,
          type: createForm.type,
        },
      });

      // Then create the hardware item
      await createHardwareItem({
        variables: {
          sku_code: createForm.code,
          total_quantity: createForm.totalQuantity,
          available_quantity: createForm.availableQuantity,
          event_slug: CURRENT_EVENT_SLUG,
        },
      });

      successToast("Item created successfully");
      setCreateModalOpen(false);

      // Refetch all relevant data
      await Promise.all([
        refetchHardwareSkus(),
        refetchHardwareItems(),
        refetchRequests(),
      ]);
    } catch (e) {
      errorToast((e as GraphQLError)?.message || "Failed to create item");
    }
  }, [
    createForm,
    createHardwareSku,
    createHardwareItem,
    refetchHardwareSkus,
    refetchHardwareItems,
    refetchRequests,
  ]);

  const handleCancelCreate = useCallback(() => {
    setCreateModalOpen(false);
  }, []);

  const handleDeleteItem = useCallback((item: THardwareInventoryItem) => {
    setDeletingItem(item);
    setDeleteModalOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!deletingItem) return;

    try {
      await deleteHardwareItem({
        variables: {
          id: deletingItem.id,
        },
      });

      successToast("Item deleted successfully");
      setDeleteModalOpen(false);
      setDeletingItem(null);

      // Refetch all relevant data
      await Promise.all([
        refetchHardwareSkus(),
        refetchHardwareItems(),
        refetchRequests(),
      ]);
    } catch (e) {
      errorToast((e as GraphQLError)?.message || "Failed to delete item");
    }
  }, [
    deletingItem,
    deleteHardwareItem,
    refetchHardwareSkus,
    refetchHardwareItems,
    refetchRequests,
  ]);

  const handleCancelDelete = useCallback(() => {
    setDeleteModalOpen(false);
    setDeletingItem(null);
  }, []);

  if (
    hardwareCategoriesDataLoading ||
    hardwareSkusDataLoading ||
    hardwareItemsLoading
  ) {
    return <Spinner text={loadingMessage} />;
  }

  if (tab === "uploadSpreadsheet") {
    return <UploadSpreadsheet tab={tab} setTab={setTab} />;
  }

  return (
    <>
      <Header
        title="Hardware"
        subtitle="Browse and request a variety of hardware to bring your project to life!"
      />
      <PageWrapper>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "24px",
            marginBottom: "48px",
          }}
        >
          <HeaderWrapper>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "4px",
              }}
            >
              <Text type="h2">Inventory Management</Text>
              <Text
                type="body1"
                style={{
                  color: globalConstants?.color?.textSecondary as string,
                }}
              >
                Add or delete items by uploading a spreadsheet.
              </Text>
            </div>
            <div style={{ display: "flex", gap: "12px" }}>
              <Button color="primary" onClick={handleCreateItem}>
                Add Item
              </Button>
              <Button
                color="secondary"
                onClick={() => setTab("uploadSpreadsheet")}
              >
                Upload Spreadsheet
              </Button>
            </div>
          </HeaderWrapper>
          <Flex>
            <SearchWrapper>
              <Search
                query={query || ""}
                onSearchChange={onSearchChange}
                totalSearchResults={filteredHardwareItems.length}
                placeholder="Search all items"
              />
            </SearchWrapper>
            <DropdownMenu
              items={[
                [
                  {
                    content: <Text type="body1">All</Text>,
                    action: () => setSelectedCategory(-1),
                    align: "left" as const, // need const for typecheck
                  },
                ],
                hardwareCategories?.map((category) => ({
                  content: <Text type="body1">{category.name}</Text>,
                  action: () => setSelectedCategory(category.id),
                  align: "left",
                })) || [],
              ]}
              style={{ justifyContent: "start" }}
            >
              <Button size="lg">
                {hardwareCategories.find(
                  (category) => category.id === selectedCategory
                )?.name || "All"}
                <ChevronDownIcon />
              </Button>
            </DropdownMenu>
          </Flex>
        </div>
        {filteredHardwareItems.length === 0 ? (
          <Text type="body1" style={{ marginTop: "24px" }}>
            No items found.
          </Text>
        ) : (
          <ItemCardsWrapper>
            {filteredHardwareItems.map((item) => (
              <div
                key={item.id}
                style={{
                  display: "flex",
                  flexDirection: "row",
                  gap: "16px",
                  border: `1px solid gray`,
                  padding: "16px",
                  height: "100%",
                  minHeight: "200px",
                }}
              >
                {/* Large Image on the left */}
                <img
                  src={item.sku.image}
                  alt={item.sku.name}
                  style={{
                    height: "120px",
                    width: "120px",
                    objectFit: "cover",
                    borderRadius: "8px",
                    flexShrink: 0,
                  }}
                />

                {/* Content on the right */}
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "12px",
                    flex: 1,
                  }}
                >
                  {/* Title with buttons */}
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "flex-start",
                      justifyContent: "space-between",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                      }}
                    >
                      <Text type="h2">{item.sku.name}</Text>
                      {item.sku.type === HardwareSkuType.Lottery && (
                        <LotteryPill>Special Item</LotteryPill>
                      )}
                    </div>
                    <div style={{ display: "flex", gap: "8px" }}>
                      <Icon
                        name="edit"
                        style={{
                          cursor: "pointer",
                          minWidth: "24px",
                          minHeight: "24px",
                          flexShrink: 0,
                          padding: "4px",
                        }}
                        onClick={() => handleEditItem(item)}
                      />
                      <Icon
                        name="x"
                        style={{
                          cursor: "pointer",
                          minWidth: "24px",
                          minHeight: "24px",
                          flexShrink: 0,
                          padding: "4px",
                          color: "#dc3545",
                        }}
                        onClick={() => handleDeleteItem(item)}
                      />
                    </div>
                  </div>

                  {/* 2x2 Grid for stats */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "12px",
                    }}
                  >
                    <div>
                      <Text
                        type="body1"
                        style={{
                          color: "black",
                        }}
                      >
                        Total: {item.total_quantity}
                      </Text>
                    </div>
                    <div>
                      <Text
                        type="body1"
                        style={{
                          color: "black",
                        }}
                      >
                        Available: {item.available_quantity}
                      </Text>
                    </div>
                    <div>
                      <Text
                        type="body1"
                        style={{
                          color: "black",
                        }}
                      >
                        Loaned Out:{" "}
                        {item.total_quantity - item.available_quantity}
                      </Text>
                    </div>
                    <div>
                      <Text
                        type="body1"
                        style={{
                          color: "black",
                        }}
                      >
                        Category:{" "}
                        {hardwareCategories?.find(
                          (cat) => cat.id === item.sku.category_id
                        )?.name || "Unknown"}
                      </Text>
                    </div>
                  </div>

                  {/* Location */}
                  {item.sku.location && (
                    <Text
                      type="body1"
                      style={{
                        color: "black",
                        fontSize: "16px",
                      }}
                    >
                      <span
                        role="img"
                        aria-label="location pin"
                        style={{ marginRight: "4px" }}
                      >
                        📍
                      </span>
                      {item.sku.location}
                    </Text>
                  )}
                </div>
              </div>
            ))}
          </ItemCardsWrapper>
        )}
      </PageWrapper>

      <Modal
        isOpen={editModalOpen}
        onClose={handleCancelEdit}
        title="Edit Hardware Item"
        width="600px"
      >
        <EditForm>
          <div
            style={{ display: "flex", flexDirection: "column", gap: "16px" }}
          >
            <InputContainer>
              <Text type="body1" style={{ fontWeight: 600 }}>
                Name
              </Text>
              <TextInput
                type="text"
                placeholder="Enter item name"
                value={editForm.name}
                onChange={(e) => {
                  const value = e.target?.value;
                  if (value !== undefined) {
                    setEditForm((prev) => ({ ...prev, name: value }));
                  }
                }}
                style={{ width: "100%" }}
              />
            </InputContainer>
            <InputContainer>
              <Text type="body1" style={{ fontWeight: 600 }}>
                Description
              </Text>
              <TextArea
                placeholder="Enter item description"
                value={editForm.description}
                onChange={(e) => {
                  const value = e.target?.value;
                  if (value !== undefined) {
                    setEditForm((prev) => ({
                      ...prev,
                      description: value,
                    }));
                  }
                }}
                fullWidth
                maxChars={500}
              />
            </InputContainer>
            <InputContainer>
              <Text type="body1" style={{ fontWeight: 600 }}>
                Image URL
              </Text>
              <TextInput
                type="text"
                placeholder="Enter image URL"
                value={editForm.image}
                onChange={(e) => {
                  const value = e.target?.value;
                  if (value !== undefined) {
                    setEditForm((prev) => ({ ...prev, image: value }));
                  }
                }}
                style={{ width: "100%" }}
              />
            </InputContainer>
            <InputContainer>
              <Text type="body1" style={{ fontWeight: 600 }}>
                Location
              </Text>
              <TextInput
                type="text"
                placeholder="Enter location"
                value={editForm.location}
                onChange={(e) => {
                  const value = e.target?.value;
                  if (value !== undefined) {
                    setEditForm((prev) => ({ ...prev, location: value }));
                  }
                }}
                style={{ width: "100%" }}
              />
            </InputContainer>
            <InputContainer>
              <Text type="body1" style={{ fontWeight: 600 }}>
                Category
              </Text>
              <Select
                fullWidth
                value={editForm.categoryId}
                onChange={(e) => {
                  const value = e.target?.value;
                  if (value !== undefined) {
                    setEditForm((prev) => ({
                      ...prev,
                      categoryId: parseInt(value) || 0,
                    }));
                  }
                }}
                placeholder="Select a category"
              >
                <Select.Option value={0}>Select a category</Select.Option>
                {hardwareCategories?.map((category) => (
                  <Select.Option key={category.id} value={category.id}>
                    {category.name}
                  </Select.Option>
                ))}
              </Select>
            </InputContainer>
            <div style={{ display: "flex", gap: "16px" }}>
              <InputContainer style={{ flex: 1 }}>
                <Text type="body1" style={{ fontWeight: 600 }}>
                  Total Quantity
                </Text>
                <TextInput
                  type="number"
                  placeholder="Enter total quantity"
                  value={editForm.totalQuantity}
                  onChange={(e) => {
                    const value = e.target?.value;
                    if (value !== undefined) {
                      setEditForm((prev) => ({
                        ...prev,
                        totalQuantity: parseInt(value) || 0,
                      }));
                    }
                  }}
                  style={{ width: "100%" }}
                />
              </InputContainer>
              <InputContainer style={{ flex: 1 }}>
                <Text type="body1" style={{ fontWeight: 600 }}>
                  Available Quantity
                </Text>
                <TextInput
                  type="number"
                  placeholder="Enter available quantity"
                  value={editForm.availableQuantity}
                  onChange={(e) => {
                    const value = e.target?.value;
                    if (value !== undefined) {
                      setEditForm((prev) => ({
                        ...prev,
                        availableQuantity: parseInt(value) || 0,
                      }));
                    }
                  }}
                  style={{ width: "100%" }}
                />
              </InputContainer>
            </div>
          </div>
          <div
            style={{
              display: "flex",
              gap: "16px",
              marginTop: "24px",
              justifyContent: "flex-end",
            }}
          >
            <Button color="secondary" onClick={handleCancelEdit}>
              Cancel
            </Button>
            <Button color="primary" onClick={handleSaveEdit}>
              Save Changes
            </Button>
          </div>
        </EditForm>
      </Modal>

      <Modal
        isOpen={createModalOpen}
        onClose={handleCancelCreate}
        title="Create Hardware Item"
        width="600px"
      >
        <EditForm>
          <InputContainer>
            <Text type="body1" style={{ fontWeight: 600 }}>
              SKU Code *
            </Text>
            <TextInput
              type="text"
              placeholder="Enter SKU code"
              value={createForm.code}
              onChange={(e) => {
                const value = e.target?.value;
                if (value !== undefined) {
                  setCreateForm((prev) => ({ ...prev, code: value }));
                }
              }}
              style={{ width: "100%" }}
            />
          </InputContainer>
          <InputContainer>
            <Text type="body1" style={{ fontWeight: 600 }}>
              Name *
            </Text>
            <TextInput
              type="text"
              placeholder="Enter item name"
              value={createForm.name}
              onChange={(e) => {
                const value = e.target?.value;
                if (value !== undefined) {
                  setCreateForm((prev) => ({ ...prev, name: value }));
                }
              }}
              style={{ width: "100%" }}
            />
          </InputContainer>
          <InputContainer>
            <Text type="body1" style={{ fontWeight: 600 }}>
              Description
            </Text>
            <TextArea
              placeholder="Enter item description"
              value={createForm.description}
              onChange={(e) => {
                const value = e.target?.value;
                if (value !== undefined) {
                  setCreateForm((prev) => ({
                    ...prev,
                    description: value,
                  }));
                }
              }}
              fullWidth
              maxChars={500}
            />
          </InputContainer>
          <InputContainer>
            <Text type="body1" style={{ fontWeight: 600 }}>
              Image URL *
            </Text>
            <TextInput
              type="text"
              placeholder="Enter image URL"
              value={createForm.image}
              onChange={(e) => {
                const value = e.target?.value;
                if (value !== undefined) {
                  setCreateForm((prev) => ({ ...prev, image: value }));
                }
              }}
              style={{ width: "100%" }}
            />
          </InputContainer>
          <InputContainer>
            <Text type="body1" style={{ fontWeight: 600 }}>
              Location *
            </Text>
            <TextInput
              type="text"
              placeholder="Enter location"
              value={createForm.location}
              onChange={(e) => {
                const value = e.target?.value;
                if (value !== undefined) {
                  setCreateForm((prev) => ({ ...prev, location: value }));
                }
              }}
              style={{ width: "100%" }}
            />
          </InputContainer>
          <div style={{ display: "flex", gap: "16px" }}>
            <InputContainer style={{ flex: 1 }}>
              <Text type="body1" style={{ fontWeight: 600 }}>
                Category *
              </Text>
              <Select
                fullWidth
                value={createForm.categoryId}
                onChange={(e) => {
                  const value = e.target?.value;
                  if (value !== undefined) {
                    setCreateForm((prev) => ({
                      ...prev,
                      categoryId: parseInt(value) || 0,
                    }));
                  }
                }}
                placeholder="Select a category"
              >
                <Select.Option value={0}>Select a category</Select.Option>
                {hardwareCategories?.map((category) => (
                  <Select.Option key={category.id} value={category.id}>
                    {category.name}
                  </Select.Option>
                ))}
              </Select>
            </InputContainer>
            <InputContainer style={{ flex: 1 }}>
              <Text type="body1" style={{ fontWeight: 600 }}>
                Type *
              </Text>
              <Select
                fullWidth
                value={createForm.type}
                onChange={(e) => {
                  const value = e.target?.value;
                  if (value !== undefined) {
                    setCreateForm((prev) => ({
                      ...prev,
                      type: value as HardwareSkuType,
                    }));
                  }
                }}
                placeholder="Select type"
              >
                <Select.Option value={HardwareSkuType.Free}>Free</Select.Option>
                <Select.Option value={HardwareSkuType.Lottery}>
                  Special
                </Select.Option>
                <Select.Option value={HardwareSkuType.Checkout}>
                  Checkout
                </Select.Option>
              </Select>
            </InputContainer>
          </div>
          <div style={{ display: "flex", gap: "16px" }}>
            <InputContainer style={{ flex: 1 }}>
              <Text type="body1" style={{ fontWeight: 600 }}>
                Total Quantity *
              </Text>
              <TextInput
                type="number"
                placeholder="Enter total quantity"
                value={createForm.totalQuantity}
                onChange={(e) => {
                  const value = e.target?.value;
                  if (value !== undefined) {
                    setCreateForm((prev) => ({
                      ...prev,
                      totalQuantity: parseInt(value) || 0,
                    }));
                  }
                }}
                style={{ width: "100%" }}
              />
            </InputContainer>
            <InputContainer style={{ flex: 1 }}>
              <Text type="body1" style={{ fontWeight: 600 }}>
                Available Quantity *
              </Text>
              <TextInput
                type="number"
                placeholder="Enter available quantity"
                value={createForm.availableQuantity}
                onChange={(e) => {
                  const value = e.target?.value;
                  if (value !== undefined) {
                    setCreateForm((prev) => ({
                      ...prev,
                      availableQuantity: parseInt(value) || 0,
                    }));
                  }
                }}
                style={{ width: "100%" }}
              />
            </InputContainer>
          </div>
          <div
            style={{
              display: "flex",
              gap: "16px",
              marginTop: "24px",
              justifyContent: "flex-end",
            }}
          >
            <Button color="secondary" onClick={handleCancelCreate}>
              Cancel
            </Button>
            <Button color="primary" onClick={handleSaveCreate}>
              Create Item
            </Button>
          </div>
        </EditForm>
      </Modal>

      <Modal
        isOpen={deleteModalOpen}
        onClose={handleCancelDelete}
        title="Delete Hardware Item"
        width="400px"
      >
        <EditForm>
          <Text type="body1" style={{ marginBottom: "16px" }}>
            Are you sure you want to delete &quot;{deletingItem?.sku.name}
            &quot;? This action cannot be undone.
          </Text>
          <div
            style={{
              display: "flex",
              gap: "16px",
              marginTop: "24px",
              justifyContent: "flex-end",
            }}
          >
            <Button color="secondary" onClick={handleCancelDelete}>
              Cancel
            </Button>
            <Button color="danger" onClick={handleConfirmDelete}>
              Delete
            </Button>
          </div>
        </EditForm>
      </Modal>
    </>
  );
};

const Flex = styled.div<{ column?: boolean; mediaQuery?: number }>`
  display: flex;
  flex-direction: ${({ column }) => (column ? "column" : "row")};
  justify-content: ${({ column }) => (column ? "flex-start" : "space-between")};
  align-items: ${({ column }) => (column ? "flex-start" : "center")};

  @media (max-width: ${({ mediaQuery }) => mediaQuery}px) {
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
  }
`;

const HeaderWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;

  @media (max-width: ${deviceBreakpoints.tablet}px) {
    gap: 16px;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
  }
`;

const ItemCardsWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0px;
  max-width: 100%;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0px;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 0px;
  }
`;

const EditForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px 0;
  width: 100%;
`;

const SearchWrapper = styled.div`
  width: 100%;
`;

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`;

const LotteryPill = styled(InfoTag).attrs({
  mods: "subtle medium nowrap white",
})`
  padding: 4px 12px;
  background: ${({ theme }) => theme.globalConstants.color.brandPrimary};
  align-self: flex-start;
`;

export default OrganizerInventory;
